/**
 * useImportPubSub - PubSub hook for bulk import progress tracking.
 *
 * Connects to the `ai_processing` hub and listens for IMPORT_* events
 * matching a specific correlation ID. Updates wizard state accordingly.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getMultiHubSocketService } from '../utils/MultiHubSocketService';
import type { ImportJobStatus, ImportPubSubEvent } from '../types/import';
import { toErrorText } from '../utils/errorMessage';

const HUB = 'ai_processing';

export interface ImportProgress {
  status: ImportJobStatus | null;
  message: string;
  itemsProcessed: number;
  totalItems: number;
  createdCount: number;
  failedCount: number;
  previewReady: boolean;
  error: string | null;
}

const INITIAL_PROGRESS: ImportProgress = {
  status: null,
  message: '',
  itemsProcessed: 0,
  totalItems: 0,
  createdCount: 0,
  failedCount: 0,
  previewReady: false,
  error: null,
};

interface UseImportPubSubOptions {
  correlationId: string | null;
  enabled?: boolean;
  onPreviewReady?: (jobUuid: string) => void;
  onCompleted?: () => void;
  onFailed?: (error: string) => void;
}

export function useImportPubSub({
  correlationId,
  enabled = true,
  onPreviewReady,
  onCompleted,
  onFailed,
}: UseImportPubSubOptions) {
  const [progress, setProgress] = useState<ImportProgress>(INITIAL_PROGRESS);
  const connectedRef = useRef(false);
  const groupRef = useRef<string | null>(null);
  const callbacksRef = useRef({ onPreviewReady, onCompleted, onFailed });

  useEffect(() => {
    callbacksRef.current = { onPreviewReady, onCompleted, onFailed };
  }, [onPreviewReady, onCompleted, onFailed]);

  const handleMessage = useCallback(
    (raw: any) => {
      const event = Array.isArray(raw) ? raw[0] : raw || {};
      const innerData = event.data || {};
      const data: Record<string, any> = { ...event, ...innerData };

      const eventCorrelationId =
        data.correlationId || data.correlation_id || data.CorrelationID || null;

      // Only process events for our correlation ID
      if (!correlationId || eventCorrelationId !== correlationId) return;

      // Use sub_type from data (mirrors template_builder pattern via emit_to_group)
      const subType = data.sub_type || data.subType || '';

      switch (subType) {
        case 'step_started':
        case 'step_progress':
          setProgress((prev) => ({
            ...prev,
            status: 'parsing',
            message: data.message || event.message || 'AI is parsing your file...',
          }));
          break;

        case 'step_completed':
          setProgress((prev) => ({
            ...prev,
            status: 'parsing',
            message: data.message || event.message || 'Processing...',
          }));
          break;

        case 'completed':
          setProgress((prev) => ({
            ...prev,
            status: 'preview_ready',
            message: 'Preview is ready!',
            previewReady: true,
            totalItems: data.total_rows || data.totalItems || prev.totalItems,
          }));
          callbacksRef.current.onPreviewReady?.(data.job_uuid || data.jobUuid || '');
          break;

        case 'failed':
        case 'step_failed':
          setProgress((prev) => ({
            ...prev,
            status: 'failed',
            error: toErrorText(data.error) || toErrorText(data.message) || toErrorText(event.message) || 'Import failed',
            message: toErrorText(data.error) || toErrorText(data.message) || toErrorText(event.message) || 'Import failed',
          }));
          callbacksRef.current.onFailed?.(toErrorText(data.error) || toErrorText(data.message) || 'Import failed');
          break;

        default:
          // Handle generic progress events
          if (data.message || event.message) {
            setProgress((prev) => ({
              ...prev,
              message: data.message || event.message,
            }));
          }
      }
    },
    [correlationId]
  );

  useEffect(() => {
    if (!enabled || !correlationId) return;

    let mounted = true;

    const connect = async () => {
      const service = getMultiHubSocketService();
      const connected = await service.connectToHub(HUB);
      if (!connected || !mounted) return;

      connectedRef.current = true;
      service.on(HUB, '*', handleMessage);

      // Join group for this import job
      const groupName = `import_${correlationId}`;
      service.emit(HUB, 'join', { group: groupName });
      groupRef.current = groupName;
    };

    connect();

    return () => {
      mounted = false;
      const service = getMultiHubSocketService();

      if (groupRef.current) {
        service.emit(HUB, 'leave', { group: groupRef.current });
        groupRef.current = null;
      }

      service.off(HUB, '*', handleMessage);
      connectedRef.current = false;
    };
  }, [enabled, correlationId, handleMessage]);

  const reset = useCallback(() => {
    setProgress(INITIAL_PROGRESS);
  }, []);

  return { progress, reset };
}
